import { Button } from '@creditornot/cb-button';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cssThemedValue, Typography as T } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import React, { useContext } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { ItemCommission } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { bulkUpdateItemPricing } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { RFFFieldErrorWrapper, WoltModal } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import validateYupSchema from 'common/utils/validateYupSchema';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkChangeCommissionTracking } from 'modules/menu/hooks/telemetryHooks';

import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  commissions: ItemCommission[] | undefined;
  onClearSelection: () => void;
  onClose: () => void;
  refetchCommissions: () => void;
  selectedIds: Set<string>;
};

type FormValues = {
  delivery: number | undefined;
  takeaway: number | undefined;
};

const initialFormValues: FormValues = {
  delivery: undefined,
  takeaway: undefined,
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  padding: 0;
  overflow-y: visible;
  background-color: ${cssThemedValue('surfaceMain')};
  border-radius: 8px;
`;

const DuplicateInfoContainer = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${cssThemedValue('surfaceMain')};
`;

const StyledInputNumber = styled(InputNumber)`
  width: 160px;
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const yupTestUpTo2DecimalPlaces = {
  name: 'upTo2DecimalPlaces',
  message: { id: 'validation.atMostNDecimals', values: { decimals: 2 } },
  test: (value: number | undefined) => value === undefined || new Decimal(value).decimalPlaces() <= 2,
};

const validationSchema = yup
  .object({
    takeaway: yup.number().min(0).max(100).test(yupTestUpTo2DecimalPlaces),
    delivery: yup.number().min(0).max(100).test(yupTestUpTo2DecimalPlaces),
  })
  .required();

export const formValuesToUpdatePayload = (
  formValues: FormValues,
  commissions: ItemCommission[],
  selectedIds: Set<string>,
) => {
  const newCommissionValues = {
    homedelivery:
      formValues.delivery || formValues.delivery === 0
        ? new Decimal(formValues.delivery).dividedBy(100).toNumber()
        : null,
    takeaway:
      formValues.takeaway || formValues.takeaway === 0
        ? new Decimal(formValues.takeaway).dividedBy(100).toNumber()
        : null,
  };

  return [...selectedIds].map(id => {
    const itemEatinCommission = commissions.find(c => c.item_id === id && c.delivery_method === 'eatin');

    return {
      item_id: id,
      commissions: {
        ...newCommissionValues,
        // Eatin as a whole is a (soon-to-be) deprecated feature, so it is not included in the form
        // The endpoint still expects to get the "eatin" value as well, so we send the existing eatin commission for the item if there is one
        eatin: itemEatinCommission ? Number(itemEatinCommission.value) : null,
      },
    };
  });
};

const BulkCommissionsChangeModal = ({
  commissions,
  onClearSelection,
  onClose,
  refetchCommissions,
  selectedIds,
}: Props) => {
  const intl = useIntl();
  const { merchantId } = useContext(MenuMetaContext);
  const trackChangeCommission = useBulkChangeCommissionTracking([...selectedIds]);

  const handleCommissionsChange = useMutation(
    (values: FormValues) => {
      return bulkUpdateItemPricing(merchantId, formValuesToUpdatePayload(values, commissions ?? [], selectedIds));
    },
    {
      onMutate: () => {
        reportBulkAction(selectedIds.size, 'BULK_CHANGE_COMMISSIONS');
      },
      onSuccess: (_, variables: FormValues, __) => {
        refetchCommissions();
        onClearSelection();
        trackChangeCommission(`${variables.takeaway}`, `${variables.delivery}`);
        toast.success(<FormattedMessage id="menu.bulkActions.commissionsChange.successDescription" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="menu.bulkActions.commissionsChange.errorDescription" />, {
          duration: Infinity,
        });
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  return (
    <Root isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.commissionsChange.title" />}
        />
      </ModalHeader>
      <DuplicateInfoContainer>
        <p>
          <T.Body3>
            <FormattedMessage id="menu-item.commissions-modal.info-about-send-email" />
          </T.Body3>
        </p>
        <p>
          <InputAlert variant="info">
            <FormattedMessage id="menu.bulkActions.commissionsChange.defaultCommissionInfoMessage" />
          </InputAlert>
        </p>
      </DuplicateInfoContainer>
      <Form<FormValues>
        initialValues={initialFormValues}
        onSubmit={values => {
          handleCommissionsChange.mutate(values);
        }}
        validate={validateYupSchema(validationSchema)}
      >
        {({ handleSubmit, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <StyledModalContent>
              <FormRow
                horizontalPadding
                label={intl.formatMessage({ id: 'delivery-types.homedelivery' })}
                labelAlign="center"
              >
                <Field<number | undefined> name="delivery">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <InputContainer>
                          <StyledInputNumber
                            {...input}
                            placeholder={intl.formatMessage({ id: 'menu-item.default-commission' })}
                            suffixText="%"
                            decimals={2}
                          />
                        </InputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow
                horizontalPadding
                label={intl.formatMessage({ id: 'delivery-types.takeaway' })}
                labelAlign="center"
              >
                <Field<number | undefined> name="takeaway">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <InputContainer>
                          <StyledInputNumber
                            {...input}
                            placeholder={intl.formatMessage({ id: 'menu-item.default-commission' })}
                            suffixText="%"
                            decimals={2}
                          />
                        </InputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
            </StyledModalContent>
            <StyledModalFooter>
              <CancelButton onClick={onClose} disabled={handleCommissionsChange.isLoading}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                disabled={hasValidationErrors || handleCommissionsChange.isLoading}
                loading={handleCommissionsChange.isLoading}
                type="submit"
                onClick={handleSubmit}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </StyledModalFooter>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default BulkCommissionsChangeModal;
